import styled from "styled-components"
import Color from "../../constants/Color"
import Logo from "../molecules/Logo"
import Spacer from "../atoms/Spacer"
import Sapling from "../molecules/Sapling"
import * as Animation from "../../functionality/Animation"
import _Button from "../atoms/Button"
import { useCallback, useEffect, useState } from "react"
import { DateTime } from "luxon"
import CascadeText from "../molecules/CascadeText"
import * as Links from "../../constants/Links"

type Props = {
    widthInPx: number
    heightInPx: number
}

const headlineSize: Animation.VariableValue = {
    end: 120,
    start: 50,
    endControl: 1600,   // Width in px
    startControl: 600,    // Width in px
}

const titleSize: Animation.VariableValue = {
    end: 30,
    start: 14,
    endControl: 1200,   // Width in px
    startControl: 600,    // Width in px
}

const logoWidth: Animation.VariableValue = {
    end: 800,
    start: 200,
    endControl: 1200,   // Width in px
    startControl: 400,    // Width in px
}

const spacerHeight: Animation.VariableValue = {
    end: 30,
    start: 10,
    endControl: 1400,   // Width in px
    startControl: 600,    // Width in px
}

const buttonFontSize: Animation.VariableValue = {
    end: 20,
    start: 14,
    endControl: 800,      // Width in px
    startControl: 600,    // Width in px
}

// Animations

const baseTweenValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 10,      // seconds
    startControl: 0,    // seconds
}

// const blurAnimation: Animation.VariableValue = {
//     end: 0,
//     start: 30,
//     endControl: 1,      // Base Tween
//     startControl: 0,    // Base Tween
// }

const logoTweenValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 0.15,    // Base Tween
    startControl: 0,    // Base Tween
}

const headlineTweenValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 0.4,      // Base Tween
    startControl: 0.15,    // Base Tween
}

// namespace HeadlineWords {
//     export const firstOpacityValues: Animation.VariableValue = {
//         end: 1,
//         start: 0,
//         endControl: 0.75,          // Headline Tween
//         startControl: 0,             // Headline Tween
//     }
//     export const secondOpacityValues: Animation.VariableValue = {
//         end: 1,
//         start: 0,
//         endControl: 0.875,      // Headline Tween
//         startControl: 0.125,    // Headline Tween
//     }
//     export const thirdOpacityValues: Animation.VariableValue = {
//         end: 1,
//         start: 0,
//         endControl: 1,               // Headline Tween
//         startControl: 0.25,        // Headline Tween
//     }
// }



namespace LineOne {
    export const tweenValues: Animation.VariableValue = {
        end: 1,
        start: 0,
        endControl: 0.5,      // Base Tween
        startControl: 0.3,    // Base Tween
    }

    export const firstSpanOpacityValues: Animation.VariableValue = {
        end: 1,
        start: 0,
        endControl: 0.5,          // LineOne Tween
        startControl: 0,          // LineOne Tween
    }

    export const secondSpanOpacityValues: Animation.VariableValue = {
        end: 1,
        start: 0,
        endControl: 0.7,        // LineOne Tween
        startControl: 0.3,    // LineOne Tween
    }

    export const thirdSpanOpacityValues: Animation.VariableValue = {
        end: 1,
        start: 0,
        endControl: 1,        // LineOne Tween
        startControl: 0.5,    // LineOne Tween
    }
}

const lineTwoOpacityValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 0.65,      // Base Tween
    startControl: 0.5,    // Base Tween
}

const lineTwoOffsetTweenValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 0.65,      // Base Tween
    startControl: 0.5,    // Base Tween
}

const lineTwoOffsetYValues: Animation.VariableValue = {
    end: 0,
    start: 10,
    endControl: 1,      // Line Two Offset Tween
    startControl: 0,    // Line Two Offset Tween
}

const lineThreeTweenValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 0.85,      // Base Tween
    startControl: 0.7,    // Base Tween
}

const buttonOpacityValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 1,        // Base Tween
    startControl: 0.85,    // Base Tween
}

const restartAnimationButtonOpacityValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 1,        // Base Tween
    startControl: 0.9,    // Base Tween
}

const Hero: React.FC<Props> = ({ widthInPx, heightInPx }) => {
    
    // Get the dimensions of the elements
    const headlineFontSizeInPx = Animation.calculateValue(headlineSize, widthInPx)
    const titleFontSizeInPx = Animation.calculateValue(titleSize, widthInPx)
    const logoWidthInPx = Animation.calculateValue(logoWidth, widthInPx)
    const singleSpacerHeightInPx = Animation.calculateValue(spacerHeight, widthInPx)
    const buttonFontSizeInPx = Animation.calculateValue(buttonFontSize, widthInPx)

    const headlineIsOneLine = (widthInPx > 600)

    // Set up the animations
    const [ animationStart, setAnimationStart ] = useState<DateTime | undefined>(DateTime.now())
    const [ tween, setTween ] = useState(0)

    useEffect(() => {

        const requestId = requestAnimationFrame(() => {
            if (animationStart) {
                
                const timeSinceStartInSeconds = DateTime.now().diff(animationStart).as("seconds")
                // console.log(timeSinceStartInSeconds)
                const updatedTween = Animation.calculateValue(baseTweenValues, timeSinceStartInSeconds)
                setTween(updatedTween)

                // Check if the animation has finished
                if (timeSinceStartInSeconds > baseTweenValues.endControl) {
                    setAnimationStart(undefined)
                }
            }
        })

        return () => cancelAnimationFrame(requestId)
    }, [animationStart, setAnimationStart, tween, setTween])

    const endAnimation = useCallback(() => {
        setAnimationStart(undefined)
        setTween(1)
    }, [ setAnimationStart, setTween ])

    // const blurInPx = Animation.calculateValue(blurAnimation, tween)
    const logoTween = Animation.calculateValue(logoTweenValues, tween)
    const headlineTween = Animation.calculateValue(headlineTweenValues, tween)
    // const headlineWordOneOpacity = Animation.calculateValue(HeadlineWords.firstOpacityValues, headlineTween)
    // const headlineWordTwoOpacity = Animation.calculateValue(HeadlineWords.secondOpacityValues, headlineTween)
    // const headlineWordThreeOpacity = Animation.calculateValue(HeadlineWords.thirdOpacityValues, headlineTween)
    const lineOneTween = Animation.calculateValue(LineOne.tweenValues, tween)
    const lineOneFirstSpanOpacity = Animation.calculateValue(LineOne.firstSpanOpacityValues, lineOneTween)
    const lineOneSecondSpanOpacity = Animation.calculateValue(LineOne.secondSpanOpacityValues, lineOneTween)
    const lineOneThirdSpanOpacity = Animation.calculateValue(LineOne.thirdSpanOpacityValues, lineOneTween)
    const lineTwoOpacity = Animation.calculateValue(lineTwoOpacityValues, tween)
    const lineTwoOffsetTween = Animation.easeOut(Animation.calculateValue(lineTwoOffsetTweenValues, tween))
    const lineTwoOffsetYInPx = Animation.calculateValue(lineTwoOffsetYValues, lineTwoOffsetTween)
    const lineThreeTween = Animation.calculateValue(lineThreeTweenValues, tween)
    const buttonOpacity = Animation.calculateValue(buttonOpacityValues, tween)
    const restartButtonOpacity = Animation.calculateValue(restartAnimationButtonOpacityValues, tween)

    return <Wrapper widthInPx={widthInPx} heightInPx={heightInPx} onClick={endAnimation}>
        <Sapling left="0" bottom="0" widthInPx={widthInPx} isAnimatingBlur={animationStart ? true : false}/>
        <TextContainer rightPaddingInPx={singleSpacerHeightInPx * 2}>
            <Spacer heightInPx={40}/>
            <Logo widthInPx={logoWidthInPx} tween={logoTween}/>
            <Spacer heightInPx={singleSpacerHeightInPx * 2}/>
            { headlineIsOneLine ? <Headline fontSizeInPx={headlineFontSizeInPx} opacity={headlineTween}>
                                    <Span /*opacity={headlineWordOneOpacity}*/>Nurture </Span>
                                    <Span /*opacity={headlineWordTwoOpacity}*/>your </Span>
                                    <Span /*opacity={headlineWordThreeOpacity}*/>insight</Span>
                                </Headline>
                                : <>
                                    <Headline fontSizeInPx={headlineFontSizeInPx} opacity={headlineTween}>Nurture</Headline>
                                    <Headline fontSizeInPx={headlineFontSizeInPx} opacity={headlineTween}>your</Headline>
                                    <Headline fontSizeInPx={headlineFontSizeInPx} opacity={headlineTween}>insight</Headline>
                                </>}
            <Spacer heightInPx={singleSpacerHeightInPx * 2}/>
            <Title fontSizeInPx={titleFontSizeInPx}>
                <Span opacity={lineOneFirstSpanOpacity}>You're here </Span>
                <Span opacity={lineOneSecondSpanOpacity}>because </Span>
                <Span opacity={lineOneThirdSpanOpacity}>you've seen something</Span>
            </Title>
            <Title fontSizeInPx={titleFontSizeInPx} opacity={lineTwoOpacity} offsetYInPx={lineTwoOffsetYInPx}>other people haven't</Title>
            <Spacer heightInPx={singleSpacerHeightInPx}/>
            <svg viewBox={`0 0 ${widthInPx} ${titleFontSizeInPx}`} width={widthInPx + "px"} height={titleFontSizeInPx * 1.5}>
                <CascadeText text="Let's shape the future together"
                             fadeDuration={0.3}
                             tween={lineThreeTween}
                             fill={Color.White}
                             fontSize={titleFontSizeInPx}
                             fontWeight={100}
                             textAnchor="end"
                             x={widthInPx}
                             y={titleFontSizeInPx}/>
            </svg>
            <Spacer heightInPx={singleSpacerHeightInPx * 2}/>
            <Button fontSizeInPx={buttonFontSizeInPx} opacity={buttonOpacity} href={Links.MailTo}>Get in touch</Button>
            <Spacer heightInPx={singleSpacerHeightInPx}/>
        </TextContainer>
        <RestartAnimationButton onClick={e => {e.stopPropagation(); setAnimationStart(DateTime.now()) }} opacity={restartButtonOpacity}>Like the animation? Do it again</RestartAnimationButton>
    </Wrapper>
}

export default Hero

const Wrapper = styled.div.attrs<{widthInPx: number, heightInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
        height: p.heightInPx + "px",
    }
}))`
    position: relative;
    background-color: ${Color.Black};
`

const TextContainer = styled.div.attrs<{rightPaddingInPx: number}>(p => ({
    style: {
        paddingRight: p.rightPaddingInPx + "px",
    }
}))`
    position: relative;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: end;

    z-index: 1;
`

const Headline = styled.div.attrs<{fontSizeInPx: number, opacity?: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        opacity: p.opacity ?? 1,
    }
}))`
    color: ${Color.White};

    font-family: "Mulish", sans-serif;
    font-weight: 100;
    letter-spacing: 0.05em;
    text-align: right;
    line-height: 1.4;
`

const Span = styled.span.attrs<{opacity?: number}>(p => ({
    style: {
        opacity: p.opacity ?? 1,
    }
}))``

const Title = styled.div.attrs<{fontSizeInPx: number, opacity?: number, offsetYInPx?: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        opacity: p.opacity ?? 1,
        transform: `translateY(${p.offsetYInPx ?? 0}px)`,
    }
}))`
    color: ${Color.White};

    font-family: "Mulish", sans-serif;
    font-weight: 100;
    letter-spacing: 0.05em;
    line-height: 1.5;
    text-align: right;
`

const Button = styled(_Button).attrs<{fontSizeInPx: number, opacity: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        opacity: p.opacity,
        pointerEvents: (p.opacity > 0.8) ? "all" : "none",
    }
}))``

const RestartAnimationButton = styled.div.attrs<{opacity: number}>(p => ({
    style: {
        opacity: p.opacity,
        pointerEvents: (p.opacity > 0.8) ? "all" : "none",
    }
}))`
    position: absolute;
    left: 10px;
    bottom: 10px;

    color: ${Color.White}bb;

    font-size: 14px;
    font-weight: 100;
    letter-spacing: 0.05em;

    cursor: pointer;
`